import { useEffect, useRef, useState } from 'react';

// ─── Numbers that count up ─────────────────────────────────────────────────────────
// Animates the first number inside a value ("PKR 1,250,000", "12", "64%") from where
// it was to where it is now — from 0 the first time it appears. Keeps the prefix,
// suffix, thousands separators and decimals. Anything without a number (or a React
// element) is shown as-is; nothing moves for people with "reduce motion" switched on.
const NUM = /-?\d[\d,]*(?:\.\d+)?/;
const reduced = () => typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function parseNumber(str) {
  const m = String(str).match(NUM);
  if (!m) return null;
  const raw = m[0];
  const decimals = raw.includes('.') ? raw.split('.')[1].length : 0;
  return { pre: String(str).slice(0, m.index), post: String(str).slice(m.index + raw.length), n: Number(raw.replace(/,/g, '')), decimals, commas: raw.includes(',') };
}
export function formatLike(n, p) {
  const fixed = n.toFixed(p.decimals);
  if (!p.commas) return p.pre + fixed + p.post;
  const [int, dec] = fixed.split('.');
  return p.pre + Number(int).toLocaleString('en-US') + (dec ? '.' + dec : '') + p.post;
}

export default function CountUp({ value, duration = 750 }) {
  const isText = typeof value === 'string' || typeof value === 'number';
  const parsed = isText ? parseNumber(value) : null;
  const target = parsed ? parsed.n : null;
  const from = useRef(0);
  const [shown, setShown] = useState(parsed && !reduced() ? 0 : target);
  useEffect(() => {
    if (target == null || !isFinite(target)) return undefined;
    if (reduced()) { setShown(target); from.current = target; return undefined; }
    const start = from.current, t0 = performance.now();
    let raf = 0;
    const tick = now => {
      const k = Math.min(1, (now - t0) / duration);
      const eased = 1 - Math.pow(1 - k, 3);
      setShown(start + (target - start) * eased);
      if (k < 1) raf = requestAnimationFrame(tick); else from.current = target;
    };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); from.current = target; };
  }, [target, duration]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!parsed) return value;
  return formatLike(shown == null ? target : shown, parsed);
}
